import Vue from "vue";
import cloneDeep from "lodash/cloneDeep";
import moment from "moment";
import { ActionTree, GetterTree, MutationTree } from "vuex";
import { RootState } from "@/store/types";
import { ServerResult } from "@/core/http/server_result";
import { fetchEnumsFromSchema } from "@/core/tools/forms/fetch_enums_from_schema";
import { generateFormObject } from "@/core/tools/forms/generate_form_object";
import { prepareFormObjectForSave } from "@/core/tools/forms/prepare_form_object_for_save";
import { buildChildrenTree } from "./buildChildrenTree";

interface ProductTypeFormState {
  loaded: boolean;
  loading: boolean;
  fatalError: boolean;
  document: any;
  form: any;
  schema: any;
  meta: any;
  childrenDocuments: any[];
  childrenLoaded: boolean;
  childrenLoading: boolean;
  savedBusinessScopes: any[];
  parentTypeColor: string | null;
  parentTypeName: string | null;
  priceSegmentColor: string | null;
  priceSegmentName: string | null;
  technologyColor: string | null;
  technologyName: string | null;
}

const state: ProductTypeFormState = {
  loading: false,
  loaded: false,
  fatalError: false,
  document: null,
  form: {},
  schema: null,
  meta: {},
  childrenDocuments: [],
  childrenLoaded: false,
  childrenLoading: false,
  savedBusinessScopes: [],
  parentTypeColor: null,
  parentTypeName: null,
  priceSegmentColor: null,
  priceSegmentName: null,
  technologyColor: null,
  technologyName: null
};

const initState = cloneDeep(state);

export const STORE_KEY = "$_product_type_form";

const COLUMNS: any = {
  id: "products_types.id",
  name: "products_types.name",
  parent_id: "products_types.parent_id",
  record_type: "products_types.record_type",
  code: "products_types.code",
  product_kind: "products_types.product_kind",
  promo_name: "products_types.promo_name",
  teaser_composition: "products_types.teaser_composition",
  scope: "products_types.scope",
  composition: "products_types.composition",
  technology_id: "products_types.technology_id",
  price_segment_id: "products_types.price_segment_id",
  trade_mark_id: "products_types.trade_mark_id",
  color: "products_types.color",
  protected_recipe: "products_types.protected_recipe",
  attachments: "products_types.attachments",
  label_attachments: "products_types.label_attachments",
  specification_attachments: "products_types.specification_attachments",
  created_at: "products_types.created_at",
  updated_at: "products_types.updated_at",
  archived_at: "products_types.archived_at"
};

const CHILDREN_COLUMNS: any = {
  id: "products.id",
  name: "products.name",
  code: "products.code",
  archived_at: "products.archived_at",
  children: "grouped_product_containers.containers"
};

function buildChildrenQuery(parentProductId: number) {
  const month = moment().format("YYYY-MM-01");

  return {
    columns: CHILDREN_COLUMNS,
    from: "s:products",
    from_as: "products",
    joins: [
      {
        join: {
          columns: {
            product_id: "product_containers.product_id",
            containers: {
              $array_agg: {
                $obj: {
                  id: "product_containers.id",
                  name: "product_containers.name",
                  container_name: "product_containers.name",
                  product_id: "product_containers.product_id",
                  weight: "product_containers.weight",
                  archived_at: "product_containers.archived_at",
                  sku_mixcart: "product_containers.sku_mixcart",
                  code: "product_containers.sku_mixcart",
                  created_at: "product_containers.created_at",
                  is_base_prime_cost: "product_containers.is_base_prime_cost",
                  cost: "prime_costs.cost",
                  trade_price: "prime_costs.trade_price",
                  retail_price: "prime_costs.retail_price",
                  sticker_names: "grouped_container_stickers.sticker_names"
                }
              }
            }
          },
          from: "s:product_containers",
          from_as: "product_containers",
          joins: [
            {
              join: "s:prime_costs",
              type: "left",
              as: "prime_costs",
              on: {
                $and: [
                  {
                    $eq: [
                      "prime_costs.product_container_id",
                      "product_containers.id"
                    ]
                  },
                  {
                    $eq: ["prime_costs.month", { $: month }]
                  }
                ]
              }
            },
            {
              join: {
                columns: {
                  product_container_id:
                    "container_stickers.product_container_id",
                  sticker_names: {
                    $array_agg: {
                      $obj: {
                        name: "voc_stickers.name"
                      }
                    }
                  }
                },
                from: "s:product_containers_stickers",
                from_as: "container_stickers",
                joins: [
                  {
                    join: "s:voc_stickers",
                    as: "voc_stickers",
                    on: {
                      $eq: ["voc_stickers.id", "container_stickers.sticker_id"]
                    }
                  }
                ],
                groups: ["container_stickers.product_container_id"]
              },
              type: "left",
              as: "grouped_container_stickers",
              on: {
                $eq: [
                  "grouped_container_stickers.product_container_id",
                  "product_containers.id"
                ]
              }
            }
          ],
          groups: ["product_containers.product_id"]
        },
        type: "left",
        as: "grouped_product_containers",
        on: { $eq: ["grouped_product_containers.product_id", "products.id"] }
      }
    ],
    where: {
      $and: [
        { $eq: ["products.type_id", { $: parentProductId }] },
        {
          // как в витрине каталога: скрытые без флага show_in_catalog дети не показываем
          $or: [
            { $eq: ["products.show_in_catalog", { $: true }] },
            { $null: ["products.show_in_catalog"] }
          ]
        }
      ]
    },
    page_size: 100000,
    order: [{ $asc: ["products.name"] }]
  };
}

export const actions: ActionTree<ProductTypeFormState, RootState> = {
  init({ commit, dispatch }, params) {
    return dispatch("loadData", params);
  },

  loadData(context, params) {
    const { commit, dispatch } = context;
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      commit("START_LOADING");

      vm.$db([
        vm
          .$q("LOAD_PRODUCT_TYPE")
          .raw("documents.get")
          .action({
            schema: "products_types",
            id: params.id,
            columns: COLUMNS
          }),
        vm
          .$q("GET_PRODUCT_TYPE_BUSINESS_SCOPES")
          .raw("documents.list")
          .action({
            columns: {
              id: "product_type_business_scopes.id",
              product_type_id: "product_type_business_scopes.product_type_id",
              business_scope_id:
                "product_type_business_scopes.business_scope_id",
              name: "business_scopes.name"
            },
            from: "s:product_type_business_scopes",
            from_as: "product_type_business_scopes",
            joins: [
              {
                join: "s:business_scopes",
                as: "business_scopes",
                on: {
                  $eq: [
                    "business_scopes.id",
                    "product_type_business_scopes.business_scope_id"
                  ]
                }
              }
            ],
            where: {
              $eq: [
                "product_type_business_scopes.product_type_id",
                { $: params.id }
              ]
            },
            page_size: 100000
          })
      ])
        .then((result: ServerResult) => {
          const content = result.getOperation("LOAD_PRODUCT_TYPE").getContent();
          const businessScopes =
            result.getOperation("GET_PRODUCT_TYPE_BUSINESS_SCOPES").getContent()
              .documents || [];
          console.debug("[ProductTypeForm.loadData]", {
            hasLabelAttachments: !!(
              content.document && content.document.label_attachments
            ),
            labelAttachmentsCount:
              (content.document &&
                content.document.label_attachments &&
                content.document.label_attachments.length) ||
              0,
            businessScopesCount: businessScopes.length
          });
          commit("SET_FORM_DATA", { ...content, businessScopes });
          commit("LOADED_SUCCESS");

          if (
            content.document &&
            content.document.record_type === "parent_product"
          ) {
            if (content.document.parent_id) {
              dispatch("loadParentTypeColor", content.document.parent_id);
            } else {
              commit("SET_PARENT_TYPE_COLOR", null);
              commit("SET_PARENT_TYPE_NAME", null);
            }
            if (content.document.price_segment_id) {
              dispatch(
                "loadPriceSegmentColor",
                content.document.price_segment_id
              );
            } else {
              commit("SET_PRICE_SEGMENT_COLOR", null);
              commit("SET_PRICE_SEGMENT_NAME", null);
            }
            if (content.document.technology_id) {
              dispatch(
                "loadTechnologyColor",
                content.document.technology_id
              );
            } else {
              commit("SET_TECHNOLOGY_COLOR", null);
              commit("SET_TECHNOLOGY_NAME", null);
            }
          } else {
            commit("SET_PARENT_TYPE_COLOR", null);
            commit("SET_PARENT_TYPE_NAME", null);
            commit("SET_PRICE_SEGMENT_COLOR", null);
            commit("SET_PRICE_SEGMENT_NAME", null);
            commit("SET_TECHNOLOGY_COLOR", null);
            commit("SET_TECHNOLOGY_NAME", null);
          }

          resolve(content.document);
        })
        .catch((error: any) => {
          console.error("[ProductTypeForm.loadData] error", error);
          commit("LOADED_ERROR");
          reject(error);
        });
    });
  },

  loadPriceSegmentColor({ commit }, segmentId) {
    const vm = (this as any)._vm;

    return vm
      .$db([
        vm
          .$q("LOAD_PRICE_SEGMENT_COLOR")
          .raw("documents.get")
          .action({
            schema: "product_price_segments",
            id: segmentId,
            columns: {
              name: "product_price_segments.name",
              color: "product_price_segments.color"
            }
          })
      ])
      .then((result: ServerResult) => {
        const content = result
          .getOperation("LOAD_PRICE_SEGMENT_COLOR")
          .getContent();
        const document = content.document || {};
        const color = document.color || null;
        const name = document.name || null;

        commit("SET_PRICE_SEGMENT_COLOR", color);
        commit("SET_PRICE_SEGMENT_NAME", name);
        return { color, name };
      })
      .catch((error: any) => {
        console.error("[ProductTypeForm.loadPriceSegmentColor] error", error);
        commit("SET_PRICE_SEGMENT_COLOR", null);
        commit("SET_PRICE_SEGMENT_NAME", null);
        return null;
      });
  },

  loadTechnologyColor({ commit }, technologyId) {
    const vm = (this as any)._vm;

    return vm
      .$db([
        vm
          .$q("LOAD_TECHNOLOGY_COLOR")
          .raw("documents.get")
          .action({
            schema: "product_technologies",
            id: technologyId,
            columns: {
              name: "product_technologies.name",
              color: "product_technologies.color"
            }
          })
      ])
      .then((result: ServerResult) => {
        const content = result
          .getOperation("LOAD_TECHNOLOGY_COLOR")
          .getContent();
        const document = content.document || {};
        const color = document.color || null;
        const name = document.name || null;

        commit("SET_TECHNOLOGY_COLOR", color);
        commit("SET_TECHNOLOGY_NAME", name);
        return { color, name };
      })
      .catch((error: any) => {
        console.error("[ProductTypeForm.loadTechnologyColor] error", error);
        commit("SET_TECHNOLOGY_COLOR", null);
        commit("SET_TECHNOLOGY_NAME", null);
        return null;
      });
  },

  loadParentTypeColor({ commit }, typeId) {
    const vm = (this as any)._vm;

    return vm
      .$db([
        vm
          .$q("LOAD_PARENT_TYPE_COLOR")
          .raw("documents.get")
          .action({
            schema: "products_types",
            id: typeId,
            columns: {
              name: "products_types.name",
              color: "products_types.color"
            }
          })
      ])
      .then((result: ServerResult) => {
        const content = result
          .getOperation("LOAD_PARENT_TYPE_COLOR")
          .getContent();
        const document = content.document || {};
        const color = document.color || null;
        const name = document.name || null;

        commit("SET_PARENT_TYPE_COLOR", color);
        commit("SET_PARENT_TYPE_NAME", name);
        return { color, name };
      })
      .catch((error: any) => {
        console.error("[ProductTypeForm.loadParentTypeColor] error", error);
        commit("SET_PARENT_TYPE_COLOR", null);
        commit("SET_PARENT_TYPE_NAME", null);
        return null;
      });
  },

  loadChildren({ commit }, parentProductId) {
    const vm = (this as any)._vm;
    const month = moment().format("YYYY-MM-01");

    return new Promise((resolve, reject) => {
      commit("START_CHILDREN_LOADING");

      vm.$db([
        vm
          .$q("LOAD_PARENT_CHILDREN")
          .raw("documents.list")
          .action(buildChildrenQuery(parentProductId))
      ])
        .then((result: ServerResult) => {
          const content = result
            .getOperation("LOAD_PARENT_CHILDREN")
            .getContent();
          const documents = content.documents || [];
          const containersCount = documents.reduce(
            (count: number, doc: any) =>
              count + ((doc.children && doc.children.length) || 0),
            0
          );

          console.debug("[ProductTypeForm.loadChildren]", {
            parentId: parentProductId,
            productsCount: documents.length,
            containersCount,
            month
          });

          commit("SET_CHILDREN_TREE", documents);
          commit("CHILDREN_LOADED_SUCCESS");
          resolve(documents);
        })
        .catch((error: any) => {
          console.error("[ProductTypeForm.loadChildren] error", error);
          commit("CHILDREN_LOADED_ERROR");
          reject(error);
        });
    });
  },

  prepareNew({ commit }, initParams) {
    const vm = (this as any)._vm;

    return new Promise((resolve, reject) => {
      commit("START_LOADING");

      vm.$db([
        vm
          .$q("LOAD_PRODUCT_TYPE_SCHEMA")
          .raw("schemas.get")
          .action({ id: "s:products_types" })
      ])
        .then((result: ServerResult) => {
          commit("GENERATE_FORM_DATA", {
            ...result.getOperation("LOAD_PRODUCT_TYPE_SCHEMA").getContent(),
            initParams
          });
          commit("LOADED_SUCCESS");
          resolve(null);
        })
        .catch((error: any) => {
          console.error("[ProductTypeForm.prepareNew] error", error);
          commit("LOADED_ERROR");
          reject(error);
        });
    });
  },

  changeFormValue({ commit, dispatch }, params) {
    commit("CHANGE_FORM_VALUE", params);

    if (params.field === "price_segment_id") {
      if (params.newValue) {
        dispatch("loadPriceSegmentColor", params.newValue);
      } else {
        commit("SET_PRICE_SEGMENT_COLOR", null);
        commit("SET_PRICE_SEGMENT_NAME", null);
      }
    }

    if (params.field === "technology_id") {
      if (params.newValue) {
        dispatch("loadTechnologyColor", params.newValue);
      } else {
        commit("SET_TECHNOLOGY_COLOR", null);
        commit("SET_TECHNOLOGY_NAME", null);
      }
    }

    if (params.field === "parent_id") {
      if (params.newValue) {
        dispatch("loadParentTypeColor", params.newValue);
      } else {
        commit("SET_PARENT_TYPE_COLOR", null);
        commit("SET_PARENT_TYPE_NAME", null);
      }
    }
  },

  addAttachments({ state }, { newValue }) {
    state.form.attachments = [
      newValue,
      ...(state.form.attachments ? state.form.attachments : [])
    ];
  },

  addLabelAttachments({ state }, { newValue }) {
    state.form.label_attachments = [
      newValue,
      ...(state.form.label_attachments ? state.form.label_attachments : [])
    ];
  },

  addSpecificationAttachments({ state }, { newValue }) {
    state.form.specification_attachments = [
      newValue,
      ...(state.form.specification_attachments
        ? state.form.specification_attachments
        : [])
    ];
  },

  saveForm({ commit, state }, id) {
    const vm = (this as any)._vm;
    const normalizedForm = prepareFormObjectForSave(state.schema, state.form);
    const selectedBusinessScopeIds = state.form.business_scope_ids || [];

    if (normalizedForm.record_type === "type") {
      normalizedForm.parent_id = null;
      normalizedForm.protected_recipe = false;
      // пустой вид — валидное состояние «не классифицирован»:
      // без явного null prepareFormObjectForSave вырезал бы ключ из payload.
      // Гвард по ключу формы (он строится по схеме сервера) не даёт отправить
      // неизвестную колонку, пока tenant-миграция не применена
      if ("product_kind" in state.form) {
        normalizedForm.product_kind = state.form.product_kind || null;
      }
    }

    delete normalizedForm.business_scope_ids;

    console.debug("[FIX][ProductTypeForm.save]", {
      record_type: normalizedForm.record_type,
      parent_id: normalizedForm.parent_id,
      protected_recipe: normalizedForm.protected_recipe,
      businessScopesCount: selectedBusinessScopeIds.length,
      payloadKeys: Object.keys(normalizedForm),
      labelAttachmentsCount:
        (normalizedForm.label_attachments &&
          normalizedForm.label_attachments.length) ||
        0,
      specificationAttachmentsCount:
        (normalizedForm.specification_attachments &&
          normalizedForm.specification_attachments.length) ||
        0
    });

    return new Promise((resolve, reject) => {
      commit("START_LOADING");

      let saveQuery;

      if (id) {
        saveQuery = vm
          .$q("SAVE_PRODUCT_TYPE")
          .update(id, normalizedForm);

        if (normalizedForm.record_type === "parent_product") {
          saveQuery = saveQuery
            .createManyRelation(
              "product_type_business_scopes",
              "product_type_id",
              (createRelated: any) => {
                const oldScopeIds = state.savedBusinessScopes.map(
                  (scope: any) => scope.business_scope_id
                );
                return selectedBusinessScopeIds
                  .filter((scopeId: any) => !oldScopeIds.includes(scopeId))
                  .map((scopeId: any) =>
                    createRelated({ business_scope_id: scopeId })
                  );
              }
            )
            .deleteManyRelation(
              "product_type_business_scopes",
              "product_type_id",
              (deleteRelated: any) => {
                return state.savedBusinessScopes
                  .filter(
                    (scope: any) =>
                      !selectedBusinessScopeIds.includes(
                        scope.business_scope_id
                      )
                  )
                  .map((scope: any) => deleteRelated(scope));
              }
            );
        }
      } else {
        saveQuery = vm
          .$q("SAVE_PRODUCT_TYPE")
          .create("products_types", normalizedForm);

        if (normalizedForm.record_type === "parent_product") {
          saveQuery = saveQuery.withManyRelation(
            "product_type_business_scopes",
            "product_type_id",
            (createRelated: any) => {
              return selectedBusinessScopeIds.map((scopeId: any) =>
                createRelated({ business_scope_id: scopeId })
              );
            }
          );
        }
      }

      vm.$db([saveQuery])
        .then((result: ServerResult) => {
          const operationResult = result.getOperation("SAVE_PRODUCT_TYPE");

          if (operationResult.hasError()) {
            console.error(
              "[ProductTypeForm.save] api error",
              operationResult.getContent()
            );
            commit("LOADED_SUCCESS");
            reject();
          } else {
            const savedContent = operationResult.getContent();
            commit("ON_FORM_SAVE", savedContent);

            if (state.form.record_type === "parent_product") {
              commit(
                "UPDATE_SAVED_BUSINESS_SCOPES",
                state.form.business_scope_ids || []
              );
            }

            commit("LOADED_SUCCESS");
            resolve(savedContent);
          }
        })
        .catch((error: any) => {
          console.error("[ProductTypeForm.save] error", error);
          reject(error);
        });
    });
  },

  reset({ commit }) {
    commit("RESET");
  }
};

export const mutations: MutationTree<ProductTypeFormState> = {
  RESET(state) {
    Object.assign(state, cloneDeep(initState));
  },

  START_LOADING: state => {
    state.loaded = false;
    state.loading = true;
    state.fatalError = false;
  },
  LOADED_SUCCESS: state => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = false;
  },
  LOADED_ERROR: state => {
    state.loaded = true;
    state.loading = false;
    state.fatalError = true;
  },

  START_CHILDREN_LOADING: state => {
    state.childrenLoading = true;
  },
  CHILDREN_LOADED_SUCCESS: state => {
    state.childrenLoading = false;
    state.childrenLoaded = true;
  },
  CHILDREN_LOADED_ERROR: state => {
    state.childrenLoading = false;
    state.childrenLoaded = false;
  },

  SET_FORM_DATA(state, { document, schema, businessScopes }) {
    state.document = document;
    state.schema = schema;
    state.meta = fetchEnumsFromSchema(schema);
    state.form = generateFormObject(schema, document);
    state.savedBusinessScopes = businessScopes || [];

    if (document && document.record_type === "parent_product") {
      state.form.business_scope_ids = (businessScopes || []).map(
        (scope: any) => scope.business_scope_id
      );
    } else {
      state.form.business_scope_ids = [];
    }
  },

  GENERATE_FORM_DATA(state, { schema, initParams }) {
    state.document = {};
    state.schema = schema;
    state.meta = fetchEnumsFromSchema(schema);
    state.form = generateFormObject(schema, {
      record_type: "type",
      ...initParams
    });
    state.savedBusinessScopes = [];

    if (state.form.record_type === "parent_product") {
      state.form.business_scope_ids = [];
    }
  },

  ON_FORM_SAVE(state, { document, schema }) {
    const currentScopeIds = state.form.business_scope_ids || [];

    state.document = document;
    state.schema = schema;
    state.form = generateFormObject(schema, document);
    state.form.business_scope_ids = currentScopeIds;
  },

  SET_CHILDREN_TREE(state, documents) {
    state.childrenDocuments = documents;
  },

  UPDATE_SAVED_BUSINESS_SCOPES(state, scopeIds) {
    state.savedBusinessScopes = scopeIds.map((scopeId: any) => ({
      business_scope_id: scopeId
    }));
  },

  SET_PARENT_TYPE_COLOR(state, color) {
    state.parentTypeColor = color;
  },

  SET_PARENT_TYPE_NAME(state, name) {
    state.parentTypeName = name;
  },

  SET_PRICE_SEGMENT_COLOR(state, color) {
    state.priceSegmentColor = color;
  },

  SET_PRICE_SEGMENT_NAME(state, name) {
    state.priceSegmentName = name;
  },

  SET_TECHNOLOGY_COLOR(state, color) {
    state.technologyColor = color;
  },

  SET_TECHNOLOGY_NAME(state, name) {
    state.technologyName = name;
  },

  CHANGE_FORM_VALUE(state, params) {
    Vue.set(state.form, params.field, params.newValue);
    if (params.field === "record_type" && params.newValue === "type") {
      Vue.set(state.form, "parent_id", null);
    }
  }
};

const getters: GetterTree<ProductTypeFormState, RootState> = {
  childrenTree(state) {
    return buildChildrenTree(state.childrenDocuments);
  }
};

export const store = {
  namespaced: true,
  state,
  actions,
  mutations,
  getters
};
